import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { stepStatus } from '../utils/workflowStages';
import type { StagesRun, StepStatus } from '../utils/workflowStages';
import { LINE_H, layoutStage } from '../utils/workflowLayout';
import type { Icon, StageCanvas } from '../utils/workflowLayout';
import { fallsThroughTo, reachableStages, stagesOf } from '../utils/workflowGraph';
import type { WorkflowNode } from '../../../types/backend';

// One drawing of a workflow, used wherever a workflow is shown: while authoring it in the Studio,
// while a run walks it, and when reading a finished run back. A run is this same drawing with
// state on it, never a second picture, so what an author sees and what an operator sees cannot
// drift apart.
//
// A DRAKON canvas for each stage, stacked downward. ../utils/workflowLayout holds the reasoning;
// the short version is that the icons were never the problem, the width was. A stage is narrow,
// so a canvas holding one stage is narrow too.
//
// Shape says what a thing IS, fill says whether the run has reached it:
//   rounded box   a step             outline   not reached
//   warning edge  needs approval     filled    reached or done
//   cut corners   a decision         accent    happening now, or waiting on the operator
//   pentagon      go to that stage, and clicking it does

export type DiagramRun = StagesRun;

export interface WorkflowDiagramProps {
  nodes: WorkflowNode[];
  /** A run walking these nodes. Omitted, the drawing is the plan with nothing on it. */
  run?: DiagramRun;
  selectedId?: string;
  onSelectStep?: (nodeId: string) => void;
  /** Narrow enough for the sidebar column. */
  dense?: boolean;
}

const WORD: Record<StepStatus, string> = {
  done: 'Done',
  current: 'Here now',
  waiting: 'Waiting for your approval',
  failed: 'Stopped here',
  skipped: 'Not on the route taken',
  pending: 'Not reached',
};

const WorkflowDiagram = memo<WorkflowDiagramProps>(
  ({ nodes, run, selectedId, onSelectStep, dense }) => {
    const theme = useTheme();
    // Each of these walks every node and every route. The component re-renders on hover, on
    // selection and on each keystroke in the editor beside it, and none of those change the plan.
    const stages = useMemo(() => stagesOf(nodes), [nodes]);
    const reachable = useMemo(() => reachableStages(nodes), [nodes]);

    // Stable, because the layout takes it as a dependency: a fresh function each render would
    // re-measure and re-place every box on every render.
    const captionFor = useMemo(
      () => (node: WorkflowNode): string | null => {
        const resolved = (run?.gates ?? []).find((q) => q.node_id === node.id && q.resolution);
        if (resolved) return resolved.resolution === 'approved' ? 'Approved' : 'Rejected';
        const chosen = (run?.decisions ?? []).find((d) => d.node_id === node.id);
        return chosen ? `Took: ${chosen.chosen_condition}` : null;
      },
      [run],
    );

    // The whole layout: text measured and every box placed, for every stage.
    const canvases = useMemo(
      () => stages.map((layer) => layoutStage(layer, nodes, { dense, captionFor })),
      [stages, nodes, dense, captionFor],
    );

    const rows = useRef(new Map<number, HTMLElement | null>());
    // Following a link should land you somewhere you can see you have landed, so the stage it
    // goes to says so for a moment rather than silently scrolling under the cursor.
    const [landedOn, setLandedOn] = useState<number | null>(null);
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Stable, because every Address and inlet icon on every canvas takes it as a handler.
    const goTo = useCallback((layer: number) => {
      rows.current.get(layer)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setLandedOn(layer);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setLandedOn(null), 1600);
    }, []);

    const skinOf = (icon: Icon) => {
      const node = icon.node;
      const status: StepStatus = node && run ? stepStatus(node, run) : 'pending';
      const accent =
        status === 'current' || status === 'waiting'
          ? theme.palette.primary.main
          : status === 'failed'
            ? theme.palette.error.main
            : status === 'done'
              ? theme.palette.success.main
              : null;
      const selected = node !== null && selectedId === node.id;
      return {
        status,
        stroke: accent ?? (selected ? theme.palette.primary.main : theme.palette.divider),
        fill: accent
          ? alpha(accent, 0.14)
          : selected
            ? alpha(theme.palette.primary.main, 0.12)
            : theme.palette.background.paper,
        text:
          node && run && status === 'pending'
            ? theme.palette.text.secondary
            : theme.palette.text.primary,
      };
    };

    const shapeFor = (icon: Icon, stroke: string, fill: string) => {
      const { x, y, w, h } = icon;
      switch (icon.kind) {
        case 'decision':
          // A DRAKON question: a box with its sides cut back, so it reads as a question at a
          // glance without costing the height a diamond costs.
          return (
            <path
              d={`M ${x + 15} ${y} L ${x + w - 15} ${y} L ${x + w} ${y + h / 2} L ${x + w - 15} ${y + h} L ${x + 15} ${y + h} L ${x} ${y + h / 2} Z`}
              fill={fill}
              stroke={stroke}
              strokeWidth={1.5}
            />
          );
        case 'address':
        case 'inlet':
          // Where the run goes next, or where one can arrive from. The same icon either way,
          // because the two are the two ends of one jump; the point says which way it runs.
          return (
            <path
              d={`M ${x} ${y} L ${x + w} ${y} L ${x + w} ${y + h - 11} L ${x + w / 2} ${y + h} L ${x} ${y + h - 11} Z`}
              fill={fill}
              stroke={stroke}
              strokeWidth={1.5}
            />
          );
        case 'end':
        case 'header':
          return (
            <rect
              x={x}
              y={y}
              width={w}
              height={h}
              rx={h / 2}
              fill={fill}
              stroke={stroke}
              strokeWidth={1.5}
            />
          );
        default:
          return (
            <rect
              x={x}
              y={y}
              width={w}
              height={h}
              rx={9}
              fill={fill}
              stroke={stroke}
              strokeWidth={1.5}
            />
          );
      }
    };

    const drawCanvas = (canvas: StageCanvas) => {
      const { layer } = canvas;
      const here = run?.current_layer === layer;
      const orphan = !reachable.has(layer);
      const landed = landedOn === layer;
      const taken = (() => {
        const decision = (nodes ?? []).find((n) => n.layer === layer && n.kind === 'decision');
        return decision
          ? run?.decisions?.find((d) => d.node_id === decision.id)?.chosen_condition
          : undefined;
      })();

      return (
        <Box
          ref={(el: HTMLElement | null) => {
            rows.current.set(layer, el);
          }}
          aria-label={`Stage ${layer}`}
          sx={{
            borderRadius: 2,
            border: 1,
            borderColor: landed
              ? 'primary.main'
              : here
                ? alpha(theme.palette.primary.main, 0.5)
                : 'divider',
            bgcolor: landed
              ? alpha(theme.palette.primary.main, 0.08)
              : 'background.default',
            transition: 'background-color 240ms, border-color 240ms',
            px: 1,
            py: 0.5,
          }}
        >
          {orphan && (
            <Typography
              variant="caption"
              sx={{ color: 'warning.main', display: 'block', textAlign: 'center', pt: 0.5 }}
            >
              No route reaches this stage, so a run never would
            </Typography>
          )}

          <Box
            component="svg"
            role="img"
            aria-label={`Stage ${layer}, drawn`}
            viewBox={`${(canvas.w - widest) / 2} 0 ${widest} ${canvas.h}`}
            width={widest}
            height={canvas.h}
            sx={{ display: 'block', mx: 'auto', maxWidth: '100%', height: 'auto' }}
          >
            {canvas.connectors.map((line) => {
              const isTaken = line.condition === null || taken === undefined
                ? null
                : taken === line.condition;
              const colour = isTaken ? theme.palette.primary.main : theme.palette.text.secondary;
              return (
                <g key={line.id} opacity={isTaken === false ? 0.35 : 1}>
                  <path
                    d={line.d}
                    fill="none"
                    stroke={colour}
                    strokeWidth={isTaken ? 2 : 1.25}
                    strokeLinejoin="round"
                    opacity={line.condition ? 1 : 0.6}
                  />
                  {line.lines.length > 0 && (
                    <>
                      {/* The condition belongs to the line below it, so it is drawn over that
                          line on its own patch of canvas. Set beside the line instead it stops
                          saying which route it names. */}
                      <rect
                        x={line.labelX - line.labelW / 2}
                        y={line.labelY - line.labelH / 2}
                        width={line.labelW}
                        height={line.labelH}
                        rx={3}
                        fill={theme.palette.background.default}
                      />
                      <text textAnchor="middle" fontSize={11.5} fill={theme.palette.text.primary}>
                        {line.lines.map((text, i) => (
                          <tspan
                            key={`${text}-${i}`}
                            x={line.labelX}
                            y={line.labelY - (line.lines.length - 1) * 6.5 + i * 13 + 4}
                          >
                            {text}
                          </tspan>
                        ))}
                      </text>
                    </>
                  )}
                </g>
              );
            })}

            {canvas.icons.map((icon) => {
              const { id, node, x, y, w, h, lines, caption } = icon;

              if (icon.kind === 'bar') {
                return (
                  <rect
                    key={id}
                    x={x}
                    y={y}
                    width={w}
                    height={h}
                    rx={2}
                    fill={theme.palette.text.secondary}
                    opacity={0.5}
                  />
                );
              }

              const structural = node === null;
              const skin = skinOf(icon);
              const isTaken =
                icon.condition === null || taken === undefined ? null : taken === icon.condition;
              const accentFill =
                icon.kind === 'header'
                  ? alpha(theme.palette.text.primary, 0.07)
                  : icon.kind === 'address' || icon.kind === 'inlet'
                    ? alpha(theme.palette.primary.main, isTaken ? 0.22 : 0.1)
                    : icon.kind === 'end'
                      ? alpha(theme.palette.text.primary, 0.07)
                      : skin.fill;
              const accentStroke =
                icon.kind === 'address' || icon.kind === 'inlet'
                  ? alpha(theme.palette.primary.main, 0.65)
                  : icon.kind === 'header' || icon.kind === 'end'
                    ? theme.palette.divider
                    : skin.stroke;
              const textColour =
                icon.kind === 'address' || icon.kind === 'inlet'
                  ? theme.palette.primary.main
                  : icon.kind === 'header'
                    ? theme.palette.text.secondary
                    : skin.text;

              const jumps =
                (icon.kind === 'address' || icon.kind === 'inlet') && icon.target !== null;
              const clickable = (!structural && onSelectStep) || jumps;
              const act = () => {
                if (jumps) goTo(icon.target as number);
                else if (node && onSelectStep) onSelectStep(node.id);
              };

              const textH = lines.length * LINE_H + (caption ? LINE_H : 0);
              const firstY = y + h / 2 - textH / 2 + LINE_H - 4;

              return (
                <g
                  key={id}
                  // The state is on the element, not only in the shape and the tint: a reader on
                  // a screen reader, and anyone who does not hover, gets the same answer.
                  aria-label={
                    jumps
                      ? icon.kind === 'inlet'
                        ? `Reached from stage ${icon.target}${icon.condition ? `, if ${icon.condition}` : ''}`
                        : icon.condition
                          ? `If ${icon.condition} → Stage ${icon.target}`
                          : `Go to stage ${icon.target}`
                      : structural
                        ? lines.join(' ')
                        : `${node.label || 'Untitled step'}${run ? `: ${WORD[skin.status]}` : ''}`
                  }
                  role={clickable ? 'button' : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  onClick={clickable ? act : undefined}
                  onKeyDown={
                    clickable
                      ? (e: React.KeyboardEvent) => {
                          if (e.key === 'Enter' || e.key === ' ') act();
                        }
                      : undefined
                  }
                  opacity={
                    isTaken === false ? 0.4 : skin.status === 'skipped' && !structural ? 0.4 : 1
                  }
                  style={{ cursor: clickable ? 'pointer' : 'default' }}
                >
                  {node?.goal && (
                    <title>
                      {[node.goal, run ? WORD[skin.status] : null].filter(Boolean).join(' · ')}
                    </title>
                  )}

                  {shapeFor(icon, accentStroke, accentFill)}

                  {/* A step somebody has to approve carries a bar down its leading edge, rather
                      than a line of text under every such box. */}
                  {icon.kind === 'gate' && (
                    <rect
                      x={x + 1.5}
                      y={y + 5}
                      width={3.5}
                      height={h - 10}
                      rx={1.75}
                      fill={theme.palette.warning.main}
                      opacity={0.9}
                    />
                  )}

                  <text
                    textAnchor="middle"
                    fontSize={
                      icon.kind === 'header'
                        ? 11
                        : icon.kind === 'address' || icon.kind === 'inlet'
                          ? 12
                          : 12.5
                    }
                    fill={textColour}
                    style={{
                      textDecoration:
                        skin.status === 'skipped' && !structural ? 'line-through' : undefined,
                      fontWeight:
                        icon.kind === 'address' ||
                        icon.kind === 'inlet' ||
                        skin.status === 'current' ||
                        skin.status === 'waiting'
                          ? 600
                          : 400,
                      letterSpacing: icon.kind === 'header' ? 0.6 : undefined,
                    }}
                  >
                    {lines.map((text, i) => (
                      <tspan key={`${text}-${i}`} x={x + w / 2} y={firstY + i * LINE_H}>
                        {icon.kind === 'header' ? text.toUpperCase() : text}
                      </tspan>
                    ))}
                  </text>
                  {caption && (
                    <text
                      x={x + w / 2}
                      y={firstY + lines.length * LINE_H}
                      textAnchor="middle"
                      fontSize={10.5}
                      fill={theme.palette.text.secondary}
                    >
                      {caption}
                    </text>
                  )}
                </g>
              );
            })}
          </Box>
        </Box>
      );
    };

    // Every canvas is as wide as the widest of them, so the skewers line up from the first
    // stage to the last. Left to its own width each canvas centred on a different axis, and a
    // drawing whose spine wanders is one the eye cannot follow down.
    const widest = Math.max(0, ...canvases.map((c) => c.w));

    return (
      <Box sx={{ width: '100%', minWidth: 0, maxWidth: widest + 24, mx: 'auto' }}>
        {canvases.map((canvas, i) => (
          <React.Fragment key={canvas.layer}>
            {i > 0 && (
              // The thread between one canvas and the next, drawn only where the run really does
              // fall from that stage into this one.
              <Box
                sx={{
                  width: '2px',
                  height: 16,
                  mx: 'auto',
                  borderRadius: 1,
                  bgcolor:
                    fallsThroughTo(stages[i - 1], nodes) === canvas.layer
                      ? 'divider'
                      : 'transparent',
                }}
              />
            )}
            {drawCanvas(canvas)}
          </React.Fragment>
        ))}
      </Box>
    );
  },
);

WorkflowDiagram.displayName = 'WorkflowDiagram';

export default WorkflowDiagram;
